// Guided setup: the anchored left rail. It REPLACES the ordinary employee section navigation while a new record is being set up.
// Round initials avatar, name, a small status badge, "HR setup" with the six steps, and a way back to the employees. Every step can be opened in any order.
// Each step is a compact two-line item: the label, and (when it needs saying) the status in TEXT - never colour alone:
//   unvisited = neutral numbered circle | needs attention = amber "!" + "Needs attention"
//   configured = blue check | not needed = blue check + "Not needed".
// On a narrow screen the steps collapse behind one button so the form keeps the whole width.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { HrSetupStep } from '../../../api/client';
import { Avatar } from '../workspace/Avatar';
import { SETUP_STEPS, isResolved, statusWords, stepIndex, type AreaStep, type StepStanding } from './setupModel';

function Check() {
  return <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5" /></svg>;
}

export function SetupRail({ name, employeeId, badge, active, standings, onSelect }: {
  name: string;
  employeeId: string;
  badge: string | null;
  active: HrSetupStep;
  standings: Record<AreaStep, StepStanding>;
  onSelect: (step: HrSetupStep) => void;
}) {
  const [open, setOpen] = useState(false);
  const activeDef = SETUP_STEPS[stepIndex(active)];

  const list = (
    <ol className="space-y-1" aria-label="HR setup steps" data-testid="setup-rail-steps">
      {SETUP_STEPS.map((s) => {
        const isActive = s.key === active;
        const standing = s.key === 'review' ? null : standings[s.key];
        const status = standing?.status ?? 'unvisited';
        const resolved = !!standing && isResolved(status);
        const words = statusWords(status);
        const amber = !!standing && status === 'needs_attention'; // stepStandings already reads any legacy 'deferred' as needs_attention
        // The review step has no standing of its own. While HR is working on a step, its row says "Current step" (the full status stays in the accessible name);
        // it is the steps HR has left that carry the amber words.
        const visible = !standing ? null : isActive ? (status === 'not_needed' ? words.visible : null) : words.visible;
        const stateAttr = !standing ? (isActive ? 'current' : 'todo') : status;
        // The circle: the current unresolved step is the filled blue number; otherwise the status decides.
        const circle = isActive && !resolved ? 'bg-[#2F7FB8] text-white'
          : resolved ? 'bg-[#4AABDE] text-white border border-[#4AABDE]'
          : amber ? 'bg-amber-100 text-amber-900 border border-amber-400'
          : 'border border-gray-300 text-gray-500 bg-white';
        const spoken = standing ? `${s.label}, ${words.spoken}${standing.unavailable ? ' (can’t be shown with your permissions)' : ''}${isActive ? ', current step' : ''}` : `${s.label}${isActive ? ', current step' : ''}`;
        return (
          <li key={s.key}>
            <button
              type="button"
              aria-current={isActive ? 'step' : undefined}
              aria-label={spoken}
              onClick={() => { setOpen(false); onSelect(s.key); }}
              className={`w-full flex items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm ${isActive ? 'bg-[#EDF5FC] text-[#0F4C75] font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
              data-testid={`rail-step-${s.key}`}
              data-state={stateAttr}
              data-status={status}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${circle}`} aria-hidden="true">
                {resolved ? <Check /> : amber && !isActive ? '!' : s.number}
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block">{s.label}</span>
                {visible && (
                  <span className={`block text-xs font-medium ${amber ? 'text-amber-800' : 'text-[#176A9B]'}`} data-testid={`rail-status-${s.key}`}>{visible}</span>
                )}
                {isActive && !visible && <span className="block text-xs font-normal text-[#176A9B]">Current step</span>}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );

  return (
    <aside className="w-full lg:w-64 flex-shrink-0 lg:sticky lg:top-4 self-start" aria-label="Guided HR setup" data-testid="setup-rail">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-3">
          <Avatar name={name.replace(/\s*\([^)]*\)\s*$/, '') || name} colorKey={employeeId} sizeClass="w-11 h-11" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 break-words" data-testid="rail-name">{name}</div>
            {badge && <span className="inline-block max-w-full break-words mt-1 text-[11px] font-medium bg-[#E3F1FB] text-[#176A9B] rounded-full px-2 py-0.5" data-testid="rail-badge">{badge}</span>}
          </div>
        </div>

        <h2 className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">HR setup</h2>
        <div className="lg:hidden">
          <button type="button" className="w-full flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="rail-toggle">
            <span>Step {activeDef.number} of {SETUP_STEPS.length}: {activeDef.label}</span>
            <span aria-hidden="true">{open ? '▴' : '▾'}</span>
          </button>
          {open && <div className="mt-2" data-testid="rail-drawer">{list}</div>}
        </div>
        <div className="hidden lg:block">{list}</div>

        <div className="mt-4 pt-3 border-t border-gray-100">
          <Link to="/hr/admin" className="text-sm text-[#176A9B] hover:underline" data-testid="rail-back-to-employees">← Back to employees</Link>
        </div>
      </div>
    </aside>
  );
}
